import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { btnPrimary, btnSecondary, card, cardBody, flagBar, REG_FONTS } from './styles';
import { getVisibleQuestions } from './questions';
import type { AnswerValue, Question, StoredAnswer, Via } from './questions';

interface QuizProps {
  via: Via;
  onComplete: (answers: Record<string, StoredAnswer>) => void;
  onBack: () => void;
}

export function Quiz({ via, onComplete, onBack }: QuizProps) {
  const { t } = useTranslation('regularizacion');
  const [answers, setAnswers] = useState<Record<string, StoredAnswer>>({});
  const [currentIndex, setCurrentIndex] = useState(0);

  const visibleQuestions = getVisibleQuestions(via, answers);
  const question = visibleQuestions[currentIndex];
  const total = visibleQuestions.length;
  const progress = ((currentIndex + 1) / total) * 100;
  const isLast = currentIndex === total - 1;
  const currentAnswer = question ? answers[question.id] : undefined;
  const hasAnswer =
    question?.type === 'multiselect'
      ? currentAnswer !== undefined // Empty array counts as answered ("ninguno")
      : currentAnswer !== undefined;

  const setSingle = useCallback(
    (value: AnswerValue) => {
      if (!question) return;
      setAnswers((prev) => ({ ...prev, [question.id]: value }));
    },
    [question]
  );

  const toggleMulti = useCallback(
    (value: AnswerValue) => {
      if (!question) return;
      setAnswers((prev) => {
        const existing = Array.isArray(prev[question.id]) ? (prev[question.id] as AnswerValue[]) : [];
        const next = existing.includes(value)
          ? existing.filter((v) => v !== value)
          : [...existing, value];
        return { ...prev, [question.id]: next };
      });
    },
    [question]
  );

  const setMultiNone = useCallback(() => {
    if (!question) return;
    setAnswers((prev) => ({ ...prev, [question.id]: [] as AnswerValue[] }));
  }, [question]);

  const handleNext = () => {
    if (isLast) {
      onComplete(answers);
    } else {
      setCurrentIndex((i) => Math.min(i + 1, total - 1));
    }
  };

  const handlePrev = () => {
    if (currentIndex === 0) {
      onBack();
    } else {
      setCurrentIndex((i) => i - 1);
    }
  };

  if (!question) return null;

  return (
    <div className={card}>
      {flagBar}
      <div className={cardBody}>
        {/* Progress */}
        <div className="mb-5">
          <p className="text-sm text-[#888] mb-2" style={{ fontFamily: REG_FONTS.body }}>
            {t('quiz.questionOf', { current: currentIndex + 1, total })}
          </p>
          <div className="h-1.5 bg-[#e0e0e0] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#2e7d32] rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <QuestionView
          question={question}
          via={via}
          answer={currentAnswer}
          onSingle={setSingle}
          onToggleMulti={toggleMulti}
          onMultiNone={setMultiNone}
          t={t}
        />

        <div className="flex justify-between mt-6">
          <button onClick={handlePrev} className={btnSecondary} style={{ fontFamily: REG_FONTS.body }}>
            {t('quiz.prev')}
          </button>
          <button
            onClick={handleNext}
            disabled={!hasAnswer}
            className={btnPrimary}
            style={{ fontFamily: REG_FONTS.body }}
          >
            {isLast ? t('quiz.finish') : t('quiz.next')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

interface QuestionViewProps {
  question: Question;
  via: Via;
  answer: StoredAnswer | undefined;
  onSingle: (value: AnswerValue) => void;
  onToggleMulti: (value: AnswerValue) => void;
  onMultiNone: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function QuestionView({ question, via, answer, onSingle, onToggleMulti, onMultiNone, t }: QuestionViewProps) {
  const qKey = `questions.${via}.${question.i18nKey}`;

  return (
    <div>
      <p
        className="text-lg font-bold text-[#1a1a1a] mb-1"
        style={{ fontFamily: REG_FONTS.body }}
      >
        {t(`${qKey}.text`)}
      </p>
      <p className="text-[13px] text-[#888] mb-4" style={{ fontFamily: REG_FONTS.body }}>
        {t(`${qKey}.hint`)}
      </p>

      <div className="flex flex-col gap-3">
        {question.type === 'yesno' ? (
          <>
            <RadioOption
              label={t('quiz.yes')}
              selected={answer === 'yes'}
              onClick={() => onSingle('yes')}
            />
            <RadioOption
              label={t('quiz.no')}
              selected={answer === 'no'}
              onClick={() => onSingle('no')}
            />
          </>
        ) : question.type === 'select' ? (
          question.options?.map((opt) => (
            <RadioOption
              key={opt.value}
              label={t(opt.i18nLabel)}
              selected={answer === opt.value}
              onClick={() => onSingle(opt.value)}
            />
          ))
        ) : (
          <>
            {question.options?.map((opt) => {
              const arr = Array.isArray(answer) ? answer : [];
              const selected = arr.includes(opt.value);
              return (
                <CheckOption
                  key={opt.value}
                  label={t(opt.i18nLabel)}
                  selected={selected}
                  onClick={() => onToggleMulti(opt.value)}
                />
              );
            })}
            <CheckOption
              label={t('quiz.opt_ninguno')}
              selected={Array.isArray(answer) && answer.length === 0}
              onClick={onMultiNone}
              variant="none"
            />
          </>
        )}
      </div>
    </div>
  );
}

interface OptionProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

function RadioOption({ label, selected, onClick }: OptionProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 w-full text-left border-2 rounded-xl py-3.5 px-4 transition-all duration-200 cursor-pointer ${
        selected
          ? 'border-[#2e7d32] bg-[#e8f5e9]'
          : 'border-[#e0e0e0] hover:border-[#2e7d32] hover:bg-[#f1f8e9]'
      }`}
    >
      <span
        className={`flex-shrink-0 w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center transition-colors ${
          selected ? 'border-[#2e7d32]' : 'border-[#bbb]'
        }`}
      >
        <span
          className={`w-3 h-3 rounded-full bg-[#2e7d32] transition-transform duration-200 ${
            selected ? 'scale-100' : 'scale-0'
          }`}
        />
      </span>
      <span className="text-[#1a1a1a] font-medium" style={{ fontFamily: REG_FONTS.body }}>
        {label}
      </span>
    </button>
  );
}

interface CheckOptionProps extends OptionProps {
  variant?: 'normal' | 'none';
}

function CheckOption({ label, selected, onClick, variant = 'normal' }: CheckOptionProps) {
  const isNone = variant === 'none';
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 w-full text-left border-2 rounded-xl py-3.5 px-4 transition-all duration-200 cursor-pointer ${
        selected
          ? isNone
            ? 'border-[#888] bg-[#f5f5f5]'
            : 'border-[#2e7d32] bg-[#e8f5e9]'
          : 'border-[#e0e0e0] hover:border-[#2e7d32] hover:bg-[#f1f8e9]'
      }`}
    >
      <span
        className={`flex-shrink-0 w-[22px] h-[22px] rounded-md border-2 flex items-center justify-center transition-colors ${
          selected ? (isNone ? 'border-[#888] bg-[#888]' : 'border-[#2e7d32] bg-[#2e7d32]') : 'border-[#bbb]'
        }`}
      >
        {selected && (
          <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M3 8l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="text-[#1a1a1a] font-medium" style={{ fontFamily: REG_FONTS.body }}>
        {label}
      </span>
    </button>
  );
}
